/** 샵관리 — 대시보드 (이번 달 요약, 재고 부족, 잔여횟수 임박 회원권, 유통기한 임박) */
import React, { useMemo, useState } from 'react';
import { PageContainer } from 'styles/common.styles';
import useTenantCode from 'hooks/useTenantCode';
import {
  monthlyPnl, thisYm, stockSummary, loadTable, passRemain, today, fmt, lotsOf, num,
} from 'shop/shopStore';
import { Cards, Card, Table, MonthBar } from 'shop/shopReportUi';

const addDays = (dateStr, d) => {
  const dt = new Date(`${dateStr}T00:00:00`);
  dt.setDate(dt.getDate() + d);
  return dt.toISOString().slice(0, 10);
};

const ShopDashboard = () => {
  const tc = useTenantCode();
  const [ym, setYm] = useState(thisYm());
  const data = useMemo(() => {
    const valid = /^\d{6}$/.test(ym);
    const products = loadTable(tc, 'product');
    const customers = new Map(loadTable(tc, 'customer').map((c) => [c.customerUid, c]));
    const services = new Map(loadTable(tc, 'service').map((s) => [s.serviceUid, s]));
    const limit = addDays(today(), 30);
    return {
      pnl: valid ? monthlyPnl(tc, ym) : null,
      low: stockSummary(tc).filter((s) => s.lowStock && s.useYn !== 'N'),
      passes: loadTable(tc, 'pass')
        .filter((p) => p.status === 'ACTIVE' && (passRemain(p) <= 2 || (p.expiryDate && p.expiryDate <= limit)))
        .map((p) => [
          customers.get(p.customerUid)?.customerNm || '-',
          services.get(p.serviceUid)?.serviceNm || '-',
          `${passRemain(p)}/${num(p.totalCnt)}회`,
          p.expiryDate || '-',
        ]),
      expiring: products.flatMap((p) => lotsOf(tc, p.productUid)
        .filter((l) => num(l.remainQty) > 0 && l.expiryDate && l.expiryDate <= limit)
        .map((l) => [p.productNm, `${fmt(l.remainQty)}${p.useUnit}`, l.expiryDate])),
    };
  }, [tc, ym]);

  const { pnl } = data;
  return (
    <PageContainer>
      <MonthBar ym={ym} setYm={setYm} />
      {pnl && (
        <Cards>
          <Card label="매출" value={pnl.revenue} sub={`${pnl.saleCount}건`} />
          <Card label="매출원가" value={pnl.cogs} />
          <Card label="월 고정비" value={pnl.expense} />
          <Card label="영업이익" value={pnl.operatingProfit} strong negative={pnl.operatingProfit < 0} />
          <Card label="당월 매입(입고)" value={pnl.purchase} sub="현금흐름 참고" />
          <Card label="시술 건수" value={pnl.recordCount} suffix="건" />
        </Cards>
      )}
      <h4 style={{ fontSize: 13, margin: '10px 0 4px' }}>⚠ 안전재고 미달 ({data.low.length})</h4>
      <Table
        head={['제품', '현재고', '안전재고']}
        rows={data.low.length ? data.low.map((s) => [s.productNm, `${fmt(s.remainQty)}${s.useUnit}`, `${fmt(s.safetyQty)}${s.useUnit}`]) : [['해당 없음', '', '']]}
      />
      <h4 style={{ fontSize: 13, margin: '10px 0 4px' }}>⏰ 잔여 2회 이하 / 만료 30일 이내 회원권 ({data.passes.length})</h4>
      <Table head={['고객', '서비스', '잔여/전체', '만료일']} rows={data.passes.length ? data.passes : [['해당 없음', '', '', '']]} />
      <h4 style={{ fontSize: 13, margin: '10px 0 4px' }}>🧴 유통기한 30일 이내 재고 ({data.expiring.length})</h4>
      <Table head={['제품', '잔량', '유통기한']} rows={data.expiring.length ? data.expiring : [['해당 없음', '', '']]} />
    </PageContainer>
  );
};

export default ShopDashboard;
